import { useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import Button from "@mui/material/Button"
import DataTable from "examples/Tables/DataTable"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useTheme } from "@mui/material/styles"
import { ToastContainer, toast } from "react-toastify"
import { Stack, CircularProgress } from "@mui/material"
import InputBase from "@mui/material/InputBase"
import Paper from "@mui/material/Paper"
import SearchIcon from "@mui/icons-material/Search"
import Papa from "papaparse"
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner"
import colors from "assets/theme/base/colors"
import "../campus/Campus.css"
import Select from "react-select"
import nodata from "assets/images/nodata.png"
import { hasAdmissionPermission } from "hooks/permissions"

const { info } = colors
const BASE_URL = process.env.REACT_APP_BASE_URL
const token = sessionStorage.getItem("token")

const FeeCollectionReport = () => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const userDepartment = JSON.parse(sessionStorage.getItem("department_id"))
  const [loading, setLoading] = useState(false)

  // Session Storage
  const user = JSON.parse(sessionStorage.getItem("user"))
  const organization_id = user ? user.organization_id : null

  // Variable Declarations
  const [feeCollectionData, setFeeCollectionData] = useState([])
  const [allCampaigns, setAllCampaigns] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [originalFeeData, setOriginalFeeData] = useState([])
  const [searchKeyword, setSearchKeyword] = useState("")
  const [summaryStats, setSummaryStats] = useState({
    totalMorning: 0,
    totalEvening: 0,
    grandTotal: 0,
    totalDepartments: 0,
    totalMorningCount: 0,
    totalEveningCount: 0,
  })

  // Format currency in PKR
  const formatPKR = (amount) => {
    return `PKR ${amount?.toLocaleString("en-PK") || "0"}`
  }

  // Calculate summary statistics
  const calculateSummaryStats = (data) => {
    let totalMorning = 0
    let totalEvening = 0
    let totalDepartments = 0
    let totalMorningCount = 0
    let totalEveningCount = 0

    data.forEach((faculty) => {
      totalDepartments += faculty.departments.length
      faculty.departments.forEach((dept) => {
        totalMorning += dept.morning_paid_amount || 0
        totalEvening += dept.evening_paid_amount || 0
        totalMorningCount += dept.morning_paid_count || 0
        totalEveningCount += dept.evening_paid_count || 0
      })
    })

    return {
      totalMorning,
      totalEvening,
      grandTotal: totalMorning + totalEvening,
      totalDepartments,
      totalMorningCount,
      totalEveningCount,
    }
  }

  // Transform hierarchical data to structured format for table display
  const transformDataForTable = (data) => {
    const structuredData = []

    data.forEach((faculty) => {
      // Add faculty header row
      structuredData.push({
        type: "faculty",
        faculty: faculty.faculty,
        department: "",
        morning_paid_amount: 0,
        evening_paid_amount: 0,
        morning_paid_count: 0,
        evening_paid_count: 0,
        total_amount: 0,
        isHeader: true,
      })

      // Calculate faculty totals
      let facultyMorningTotal = 0
      let facultyEveningTotal = 0
      let facultyMorningCountTotal = 0
      let facultyEveningCountTotal = 0

      // Add department rows
      faculty.departments.forEach((dept) => {
        facultyMorningTotal += dept.morning_paid_amount || 0
        facultyEveningTotal += dept.evening_paid_amount || 0
        facultyMorningCountTotal += dept.morning_paid_count || 0
        facultyEveningCountTotal += dept.evening_paid_count || 0

        structuredData.push({
          type: "department",
          faculty: faculty.faculty,
          department: dept.department,
          morning_paid_amount: dept.morning_paid_amount || 0,
          evening_paid_amount: dept.evening_paid_amount || 0,
          morning_paid_count: dept.morning_paid_count || 0,
          evening_paid_count: dept.evening_paid_count || 0,
          total_amount: (dept.morning_paid_amount || 0) + (dept.evening_paid_amount || 0),
          isHeader: false,
        })
      })

      // Add faculty total row
      structuredData.push({
        type: "faculty_total",
        faculty: faculty.faculty,
        department: "TOTAL",
        morning_paid_amount: facultyMorningTotal,
        evening_paid_amount: facultyEveningTotal,
        morning_paid_count: facultyMorningCountTotal,
        evening_paid_count: facultyEveningCountTotal,
        total_amount: facultyMorningTotal + facultyEveningTotal,
        isTotal: true,
      })
    })

    return structuredData
  }

  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase()
    setSearchKeyword(searchTerm)

    if (!searchTerm) {
      setFeeCollectionData(originalFeeData)
      return
    }

    const filteredData = originalFeeData.filter((record) => {
      // Don't filter out faculty headers and totals, only filter departments
      if (record.type === "faculty" || record.type === "faculty_total") {
        return true
      }

      return (
        (record.faculty && record.faculty.toLowerCase().includes(searchTerm)) ||
        (record.department && record.department.toLowerCase().includes(searchTerm))
      )
    })

    setFeeCollectionData(filteredData)
  }

  const handleExportCSV = () => {
    if (feeCollectionData.length === 0) {
      toast.error("No data to export")
      return
    }

    // Create hierarchical CSV structure
    const csvRows = []

    // Add main headers
    csvRows.push([
      "Faculty/Department",
      "Morning Paid Amount (PKR)",
      "Evening Paid Amount (PKR)",
      "Total Amount (PKR)",
      "Morning Paid Count",
      "Evening Paid Count",
    ])

    // Group data back by faculty for export
    const facultyGroups = {}
    feeCollectionData.forEach((record) => {
      if (!facultyGroups[record.faculty]) {
        facultyGroups[record.faculty] = []
      }
      facultyGroups[record.faculty].push(record)
    })

    // Build CSV with faculty headers and department rows
    Object.keys(facultyGroups).forEach((facultyName) => {
      // Add faculty header row
      csvRows.push([`${facultyName}`, "", "", "", "", ""])

      // Calculate faculty totals
      let facultyMorningTotal = 0
      let facultyEveningTotal = 0
      let facultyMorningCountTotal = 0
      let facultyEveningCountTotal = 0

      // Add department rows under faculty
      facultyGroups[facultyName].forEach((dept) => {
        facultyMorningTotal += dept.morning_paid_amount
        facultyEveningTotal += dept.evening_paid_amount
        facultyMorningCountTotal += dept.morning_paid_count
        facultyEveningCountTotal += dept.evening_paid_count

        csvRows.push([
          `  ${dept.department}`, // Indent department names
          dept.morning_paid_amount,
          dept.evening_paid_amount,
          dept.total_amount,
          dept.morning_paid_count,
          dept.evening_paid_count,
        ])
      })

      // Add faculty total row
      csvRows.push([
        `${facultyName} - TOTAL`,
        facultyMorningTotal,
        facultyEveningTotal,
        facultyMorningTotal + facultyEveningTotal,
        facultyMorningCountTotal,
        facultyEveningCountTotal,
      ])

      // Add empty row for separation
      csvRows.push(["", "", "", "", "", ""])
    })

    // Add grand total row
    csvRows.push([
      "GRAND TOTAL",
      summaryStats.totalMorning,
      summaryStats.totalEvening,
      summaryStats.grandTotal,
      summaryStats.totalMorningCount,
      summaryStats.totalEveningCount,
    ])

    // Convert to CSV format
    const csv = Papa.unparse(csvRows)

    // Create a blob and download the CSV
    const blob = new Blob([csv], { type: "text/csv" })
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    link.download = `FeeCollectionReport_${selectedCampaign?.label || "Campaign"}.csv`
    link.click()

    toast.success("CSV exported successfully")
  }

  // API calls
  const fetchAllCampaigns = async () => {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
      const response = await fetch(BASE_URL + `/campaign/get_campaigns`, requestOptions)
      const data = await response.json()
      setAllCampaigns(data.campaigns)

      // Auto-select first active campaign if userDepartment exists
      if (userDepartment && data.campaigns && data.campaigns.length > 0) {
        const activeCampaigns = data.campaigns.filter((camp) => camp.status === 1)
        if (activeCampaigns.length > 0) {
          const firstActiveCampaign = activeCampaigns[0]
          const newSelectedCampaign = {
            value: firstActiveCampaign.id,
            label: firstActiveCampaign.name,
          }

          setSelectedCampaign(newSelectedCampaign)
        }
      }
    } catch (error) {
      console.log("Error fetching campaigns:", error)
      toast.error("Failed to fetch campaigns")
    }
  }

  const getFeeCollectionReport = async () => {
    // Check if campaign is selected
    if (!selectedCampaign) {
      toast.error("Please select a campaign")
      return
    }

    setLoading(true)

    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }

      const response = await fetch(
        BASE_URL + `/fee_voucher/fee_collection_report/${selectedCampaign.value}`,
        requestOptions,
      )
      const result = await response.json()

      if (response.ok) {
        const hierarchicalData = result || []
        const flatData = transformDataForTable(hierarchicalData)
        const stats = calculateSummaryStats(hierarchicalData)

        setFeeCollectionData(flatData)
        setOriginalFeeData(flatData)
        setSummaryStats(stats)

        toast.success(`Fee collection report generated successfully`)
      } else {
        toast.error(result.error || "Failed to fetch fee collection report")
        setFeeCollectionData([])
        setOriginalFeeData([])
        setSummaryStats({
          totalMorning: 0,
          totalEvening: 0,
          grandTotal: 0,
          totalDepartments: 0,
          totalMorningCount: 0,
          totalEveningCount: 0,
        })
      }
    } catch (error) {
      console.error("API Error:", error)
      toast.error("An error occurred while fetching fee collection report")
      setFeeCollectionData([])
      setOriginalFeeData([])
      setSummaryStats({
        totalMorning: 0,
        totalEvening: 0,
        grandTotal: 0,
        totalDepartments: 0,
        totalMorningCount: 0,
        totalEveningCount: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle campaign selection change
  const handleCampaignChange = (selectedOption) => {
    setSelectedCampaign(selectedOption)
    // Clear previous data when campaign changes
    setFeeCollectionData([])
    setOriginalFeeData([])
    setSearchKeyword("")
    setSummaryStats({
      totalMorning: 0,
      totalEvening: 0,
      grandTotal: 0,
      totalDepartments: 0,
      totalMorningCount: 0,
      totalEveningCount: 0,
    })
  }

  // Table Configuration
  const columns = [
    { Header: "Faculty/Department", accessor: "faculty_department", align: "left" },
    { Header: "Morning Paid", accessor: "morning_paid", align: "right" },
    { Header: "Evening Paid", accessor: "evening_paid", align: "right" },
    { Header: "Total Amount", accessor: "total_amount", align: "right" },
    { Header: "Morning Paid Count", accessor: "morning_paid_count", align: "right" },
    { Header: "Evening Paid Count", accessor: "evening_paid_count", align: "right" },
  ]

  const rows =
    feeCollectionData.length > 0
      ? feeCollectionData.map((record, index) => {
          if (record.type === "faculty") {
            // Faculty header row
            return {
              faculty_department: (
                <MDTypography variant="h6" fontWeight="bold" color="primary" sx={{ py: 1 }}>
                  {record.faculty}
                </MDTypography>
              ),
              morning_paid: "",
              evening_paid: "",
              total_amount: "",
              morning_paid_count: "",
              evening_paid_count: "",
            }
          } else if (record.type === "faculty_total") {
            // Faculty total row
            return {
              faculty_department: (
                <MDTypography variant="body1" fontWeight="bold" color="dark" sx={{ pl: 2, fontStyle: "italic" }}>
                  {record.faculty} - TOTAL
                </MDTypography>
              ),
              morning_paid: (
                <MDTypography variant="body1" fontWeight="bold" color="success">
                  {formatPKR(record.morning_paid_amount)}
                  {record.morning_paid_count > 0 && (
                    <span style={{ fontSize: "0.8em", color: "#666", marginLeft: "4px" }}>
                      ({record.morning_paid_count})
                    </span>
                  )}
                </MDTypography>
              ),
              evening_paid: (
                <MDTypography variant="body1" fontWeight="bold" color="info">
                  {formatPKR(record.evening_paid_amount)}
                  {record.evening_paid_count > 0 && (
                    <span style={{ fontSize: "0.8em", color: "#666", marginLeft: "4px" }}>
                      ({record.evening_paid_count})
                    </span>
                  )}
                </MDTypography>
              ),
              total_amount: (
                <MDTypography variant="body1" fontWeight="bold" color="dark">
                  {formatPKR(record.total_amount)}
                </MDTypography>
              ),
              morning_paid_count: (
                <MDTypography variant="body1" fontWeight="bold" color="success">
                  {record.morning_paid_count}
                </MDTypography>
              ),
              evening_paid_count: (
                <MDTypography variant="body1" fontWeight="bold" color="info">
                  {record.evening_paid_count}
                </MDTypography>
              ),
            }
          } else {
            // Department row
            return {
              faculty_department: (
                <MDTypography variant="body2" sx={{ pl: 4 }}>
                  • {record.department}
                  {(record.morning_paid_count > 0 || record.evening_paid_count > 0) && (
                    <span style={{ fontSize: "0.85em", color: "#888", marginLeft: "6px" }}>
                      (M: {record.morning_paid_count || 0}, E: {record.evening_paid_count || 0})
                    </span>
                  )}
                </MDTypography>
              ),
              morning_paid: (
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  color={record.morning_paid_amount > 0 ? "success" : "text"}
                >
                  {formatPKR(record.morning_paid_amount)}
                  {record.morning_paid_count > 0 && (
                    <span style={{ fontSize: "0.8em", color: "#666", marginLeft: "4px" }}>
                      ({record.morning_paid_count})
                    </span>
                  )}
                </MDTypography>
              ),
              evening_paid: (
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  color={record.evening_paid_amount > 0 ? "info" : "text"}
                >
                  {formatPKR(record.evening_paid_amount)}
                  {record.evening_paid_count > 0 && (
                    <span style={{ fontSize: "0.8em", color: "#666", marginLeft: "4px" }}>
                      ({record.evening_paid_count})
                    </span>
                  )}
                </MDTypography>
              ),
              total_amount: (
                <MDTypography variant="body2" fontWeight="medium" color="dark">
                  {formatPKR(record.total_amount)}
                </MDTypography>
              ),
              morning_paid_count: (
                <MDTypography variant="body2" fontWeight="medium" color="success">
                  {record.morning_paid_count}
                </MDTypography>
              ),
              evening_paid_count: (
                <MDTypography variant="body2" fontWeight="medium" color="info">
                  {record.evening_paid_count}
                </MDTypography>
              ),
            }
          }
        })
      : [
          {
            faculty_department: null,
            morning_paid: null,
            evening_paid: null,
            total_amount: null,
            morning_paid_count: null,
            evening_paid_count: null,
          },
        ]

  // Initial data loading
  useEffect(() => {
    fetchAllCampaigns()
  }, [])

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className="mb-4">
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Fee Collection Report Filters
                </MDTypography>
                <Grid container spacing={2}>
                  {/* Campaign Filter */}
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Campaign
                    </MDTypography>
                    <Select
                      name="campaign_id"
                      options={allCampaigns.map((camp) => ({
                        value: camp.id,
                        label: camp.name,
                      }))}
                      className="basic-select"
                      classNamePrefix="select"
                      placeholder="Select Campaign"
                      value={selectedCampaign}
                      onChange={handleCampaignChange}
                      isClearable
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={getFeeCollectionReport}
                      disabled={loading || !selectedCampaign}
                      sx={{ mt: 2 }}
                      style={{ color: "white" }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Generate Report"}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            {/* Summary Statistics Card */}
            {feeCollectionData.length > 0 && (
              <Card className="mb-4">
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Collection Summary
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDBox textAlign="center">
                        <MDTypography variant="h4" fontWeight="bold" color="success">
                          {formatPKR(summaryStats.totalMorning)}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Morning Collection
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDBox textAlign="center">
                        <MDTypography variant="h4" fontWeight="bold" color="info">
                          {formatPKR(summaryStats.totalEvening)}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Evening Collection
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDBox textAlign="center">
                        <MDTypography variant="h4" fontWeight="bold" color="dark">
                          {formatPKR(summaryStats.grandTotal)}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Grand Total Collection
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDBox textAlign="center">
                        <MDTypography variant="h4" fontWeight="bold" color="warning">
                          {summaryStats.totalDepartments}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Departments
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDBox textAlign="center">
                        <MDTypography variant="h4" fontWeight="bold" color="success">
                          {summaryStats.totalMorningCount}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Morning Paid Vouchers
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDBox textAlign="center">
                        <MDTypography variant="h4" fontWeight="bold" color="info">
                          {summaryStats.totalEveningCount}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Evening Paid Vouchers
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            )}

            <Card>
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <MDTypography variant="h6" fontWeight="medium">
                    Fee Collection Report by Department
                    {selectedCampaign && (
                      <MDTypography variant="body2" color="text" ml={1} component="span">
                        - {selectedCampaign.label}
                      </MDTypography>
                    )}
                  </MDTypography>

                  <Stack direction="row" spacing={2}>
                    <Paper component="form" className="search-paper">
                      <InputBase
                        sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                        placeholder="Search faculty or department..."
                        inputProps={{ "aria-label": "search fee collection records" }}
                        value={searchKeyword}
                        onChange={handleSearchKeywordChange}
                      />
                      <SearchIcon className="search-icon" />
                    </Paper>
                    {hasAdmissionPermission("FeeCollectionReport", "EXPORT CSV") && feeCollectionData.length > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        endIcon={<DocumentScannerIcon />}
                        onClick={handleExportCSV}
                        style={{ color: "white" }}
                      >
                        Export CSV
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </MDBox>

              <MDBox pt={3} pb={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : feeCollectionData && feeCollectionData.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <img
                      src={nodata || "/placeholder.svg"}
                      style={{
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No Data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      {selectedCampaign
                        ? "No fee collection records found for the selected campaign."
                        : "Please select a campaign to view fee collection report."}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default FeeCollectionReport
